import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Grid,
  Typography,
  Radio,
  FormControlLabel,
  Button,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { mergePlayers } from "../../../../api/index.js";
import useStyles from "../../../../styles/global-mui.js";
import DsasButton from "../../../components/DsasButton/DsasButton.js";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import DatePicker_Dsas from "../../../../dsasComponents/DatePicker_Dsas.js";



const MergePlayersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();

  const returnTo = location.state?.returnTo || "/sports/players";
  const mergedPlayer = location.state?.mergedPlayer || {};
  const idsToDelete = location.state?.idsToDelete || [];

  const matchesUpdated = location.state?.matchesUpdated || 0;
  const gamefactsUpdated = location.state?.gamefactsUpdated || 0;

  const [teamsSeasonDraft, setTeamsSeasonDraft] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  // ---------------------------
  // Helpers
  // ---------------------------
  const isNationalTeam = (team) => Boolean(team?.isCountry);

  const asDateOrNull = (v) => {
    if (!v) return null;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const normalizeDate = (v) => {
    const d = asDateOrNull(v);
    if (!d) return null;
    const nd = new Date(d);
    nd.setHours(12, 0, 0, 0);
    return nd;
  };

  // “jour - 1” en gardant l’heure à midi pour éviter DST/timezone
  const minusOneDay = (date) => {
    const d = normalizeDate(date);
    if (!d) return null;
    d.setDate(d.getDate() - 1);
    return d;
  };

  const clampEndDate = (startDate, endDate) => {
    if (!startDate || !endDate) return endDate;
    return endDate < startDate ? startDate : endDate;
  };

  const stableTeamsSeasonKey = (ts, idx) =>
    ts?._id ||
    ts?.team?._id ||
    `${ts?.team?.name ?? "team"}-${ts?.startDate?.toISOString?.() ?? idx}`;

  const safeTime = (d) => (d instanceof Date ? d.getTime() : NaN);
  const sortByStartDesc = (a, b) => {
    const ta = safeTime(a.startDate);
    const tb = safeTime(b.startDate);
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1; // a après
    if (Number.isNaN(tb)) return -1;
    return tb - ta;
  };

  /**
   * Applique les règles métier de fusion sur les teamsSeason :
   * - Tri par startDate décroissante (récent -> ancien)
   * - Équipe actuelle sélectionnée (selectedTeam) => endDate = null
   * - Équipes nationales => inchangées
   * - Date d’ancrage = startDate la plus récente parmi les clubs (hors nationales)
   * - Tous les autres clubs (non actuels) => endDate = J-1 de la date d’ancrage
   * - Cas particulier demandé : si le club le plus récent n’est PAS l’équipe actuelle,
   *   alors son endDate = date de fusion (aujourd’hui, normalisée à midi)
   *
   * Note :
   * - Cette logique privilégie la cohérence métier de la fusion et peut écraser
   *   des endDate existantes sur les clubs non actuels (hors nationales).
   *  Cette fonction est appelée :
   * - au chargement initial
   * - lors d’un changement d’équipe actuelle
   * Elle n’est pas appelée lors d’une édition manuelle de date
   * afin de laisser l’utilisateur ajuster les endDate.
  */
  const applyCoherentEndDates = (list, currentTeamId) => {
    const sorted = [...list].sort(sortByStartDesc);

    const clubs = sorted.filter((ts) => !isNationalTeam(ts.team) && ts.startDate);
    const latestClub = clubs[0] || null;

    const anchorDate = latestClub?.startDate || null;
    const anchorMinusOne = anchorDate ? minusOneDay(anchorDate) : null;

    const fusionDate = normalizeDate(new Date()); // aujourd’hui à midi

    return sorted.map((ts) => {
      if (isNationalTeam(ts.team)) return ts;

      const isCurrent = ts.team?._id === currentTeamId;
      const isLatest = latestClub && ts.team?._id === latestClub.team?._id;

      if (isCurrent) {
        return { ...ts, endDate: null, _autoEndDate: false };
      }

      if (!ts.startDate) return ts;

      // Cas particulier demandé par le client
      if (isLatest) {
        return {
          ...ts,
          endDate: clampEndDate(ts.startDate, fusionDate),
          _autoEndDate: true,
        };
      }

      // Règle générale
      if (!anchorMinusOne) return ts;

      return {
        ...ts,
        endDate: clampEndDate(ts.startDate, anchorMinusOne),
        _autoEndDate: true,
      };
    });
  };

  const mergeDuplicateTeamSeasons = (list) => {
    const byTeam = new Map();

    for (const ts of list) {
      const teamId = ts?.team?._id;
      if (!teamId) continue;

      const prev = byTeam.get(teamId);

      if (!prev) {
        byTeam.set(teamId, { ...ts });
        continue;
      }

      const prevStart = prev.startDate;
      const curStart = ts.startDate;
      const prevEnd = prev.endDate;
      const curEnd = ts.endDate;

      // startDate = plus vieille
      const startDate =
        prevStart && curStart ? (prevStart < curStart ? prevStart : curStart) : prevStart || curStart || null;

      //(= “endDate non-null wins, et si les deux sont non-null on prend la plus récente”)
      let endDate = null;
      if (prevEnd && curEnd) endDate = prevEnd > curEnd ? prevEnd : curEnd;
      else if (prevEnd && !curEnd) endDate = prevEnd;
      else if (!prevEnd && curEnd) endDate = curEnd;
      else endDate = null;

      // On fusionne en gardant les infos "team" (identiques)
      byTeam.set(teamId, {
        ...prev,
        startDate,
        endDate,
        // comme c'est une consolidation, on considère endDate "non auto" au départ
        _autoEndDate: false,
      });
    }

    return Array.from(byTeam.values());
  };

  

  // ---------------------------
  // Init draft + default current club
  // ---------------------------
  useEffect(() => {
    const initialRaw = (mergedPlayer.teamsSeason || []).map((ts) => ({
      ...ts,
      startDate: normalizeDate(ts.startDate),
      endDate: normalizeDate(ts.endDate),
      _autoEndDate: false,
    }));

    const initialDeduped = mergeDuplicateTeamSeasons(initialRaw);
    const initial = initialDeduped.sort(sortByStartDesc);

    const clubsSorted = initial.filter((ts) => !isNationalTeam(ts.team) && ts.startDate);
    const defaultCurrent = clubsSorted.length ? clubsSorted[0].team._id : null;

    const chosen = selectedTeam ?? defaultCurrent;

    setSelectedTeam(chosen);
    setTeamsSeasonDraft(applyCoherentEndDates(initial, chosen));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedPlayer.teamsSeason]);

  // Recalcule les endDate à chaque changement d’équipe actuelle selon les règles de fusion
  useEffect(() => {
  if (!teamsSeasonDraft.length) return;
  setTeamsSeasonDraft((prev) => applyCoherentEndDates(prev, selectedTeam));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedTeam]);

  // ---------------------------
  // Handlers
  // ---------------------------
  const handleSelectTeam = (teamId) => {
    setSelectedTeam(teamId);
  };

  const handleUpdateTeamSeasonDate = (index, field, date) => {
    const newDate = normalizeDate(date);

    setTeamsSeasonDraft((prev) => {
      const next = [...prev];
      const current = next[index];

      const updated = { ...current, [field]: newDate };

      // Cohérence intra-ligne uniquement (sans recalcul global)
      if (field === "endDate") {
        updated.endDate = clampEndDate(updated.startDate, updated.endDate);
        updated._autoEndDate = false; // modif manuelle
      }

      if (field === "startDate") {
        if (updated.endDate) {
          updated.endDate = clampEndDate(updated.startDate, updated.endDate);
        }
        // si la ligne est l'équipe actuelle, on garde endDate = null
        if (!isNationalTeam(updated.team) && updated.team?._id === selectedTeam) {
          updated.endDate = null;
        }
      }

      next[index] = updated;

      // On garde juste fusion + tri, sans réappliquer les règles auto
      return mergeDuplicateTeamSeasons(next).sort(sortByStartDesc);
    });
  };

  const executeMerge = () => setOpenConfirmation(true);

  // ---------------------------
  // Derived UI state
  // ---------------------------
  const clubs = useMemo(
    () => teamsSeasonDraft.filter((ts) => !isNationalTeam(ts.team)),
    [teamsSeasonDraft]
  );

  const mustSelectCurrentClub = clubs.length > 0;

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={0} md={4}></Grid>
        <Grid item xs={12} md={4}>
          <Box className="centered-medium-wrapper">
            <h1 className="page-title">Détails du Joueur Fusionné</h1>
          </Box>

          <Box elevation={3} sx={{ padding: 2 }}>
            {[
              { label: "Prénom", value: mergedPlayer.firstname },
              { label: "Nom", value: mergedPlayer.lastname },
              { label: "Discipline", value: mergedPlayer.discipline },
              { label: "Numéro", value: mergedPlayer.number },
              {
                label: "Date de Naissance",
                value: mergedPlayer.birthday
                  ? new Date(mergedPlayer.birthday).toLocaleDateString("fr-FR")
                  : "N/A",
              },
              { label: "Nationalité", value: mergedPlayer.nationality },
              { label: "Taille (cm)", value: mergedPlayer.size },
              { label: "Poids (kg)", value: mergedPlayer.weight },
              { label: "Position", value: mergedPlayer.positioning },
              { label: "ID Externe", value: mergedPlayer.thirdPartyId },
              { label: "Entité Externe", value: mergedPlayer.thirdPartyEntityId },
            ].map((field, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <Typography className={classes.customTypoColor}>
                  {field.label} :
                </Typography>
                <Typography className={classes.customTypoColor}>
                  {field.value || "N/A"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid item xs={0} md={4}></Grid>
      </Grid>

      <Box className="padding-wrapper xxl-padding-left xxl-padding-right">
        <Box className="centered-medium-wrapper">
          <h3 className="page-title">Historique des Transferts</h3>
        </Box>

        {teamsSeasonDraft.map((teamInfo, idx) => {
          const key = stableTeamsSeasonKey(teamInfo, idx);
          const isCurrentClub =
            !isNationalTeam(teamInfo.team) && teamInfo.team?._id === selectedTeam;

          return (
            <Box
              key={key}
              sx={{
                border: "1px solid #ccc",
                padding: "16px",
                marginBottom: idx < teamsSeasonDraft.length - 1 ? "16px" : "0",
                backgroundColor: teamInfo.endDate ? "#f0f0f0" : "white",
                position: "relative",
                minHeight: "35px",
                alignContent: "center",
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                {/* Team name */}
                <Grid item xs={12} sm={4} className="centered-column-wrapper">
                  <Typography className={classes.customTypoColor} fontWeight="bold">
                    {teamInfo.team?.name}
                  </Typography>
                </Grid>

                {/* Dates (editable) */}
                <Grid item xs={12} sm={4} className="centered-column-wrapper">
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <DatePicker_Dsas
                        label="Début"
                        value={asDateOrNull(teamInfo.startDate)}
                        onChange={(d) => handleUpdateTeamSeasonDate(idx, "startDate", d)}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <DatePicker_Dsas
                        label="Fin"
                        value={asDateOrNull(teamInfo.endDate)}
                        disabled={isCurrentClub} // fin désactivée pour le club actuel (optionnel)
                        onChange={(d) => handleUpdateTeamSeasonDate(idx, "endDate", d)}
                      />
                    </Grid>
                    {/* Petit indicateur si fin auto */}
                  {/* {!isNationalTeam(teamInfo.team) && teamInfo._autoEndDate && !isCurrentClub && (
                    <Typography
                      className={classes.customTypoColor}
                      fontSize="0.75rem"
                      fontStyle="italic"
                      sx={{ marginTop: "6px" }}
                    >
                      Fin calculée automatiquement (modifiable)
                    </Typography>
                  )} */}
                  </Grid>

                  
                </Grid>

                {/* Current team selection / National info */}
                <Grid item xs={12} sm={4} className="centered-column-wrapper">
                  {isNationalTeam(teamInfo.team) ? (
                    <Typography
                      className={classes.customTypoColor}
                      fontStyle="italic"
                      color="textSecondary"
                    >
                      (Équipe Nationale)
                    </Typography>
                  ) : (
                    <FormControlLabel
                      className={classes.customTypoColor}
                      control={
                        <Radio
                          checked={selectedTeam === teamInfo.team?._id}
                          onChange={() => handleSelectTeam(teamInfo.team?._id)}
                        />
                      }
                      label="Équipe actuelle"
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Box>

      <Box className="centered-wrapper xxl-padding-left xxl-padding-right">
        <Typography className={classes.customTypoColor} fontStyle="italic">
          Pour cette fusion vous allez :
        </Typography>
      </Box>

      <Box className="padding-wrapper xxl-padding-left xxl-padding-right">
        <Grid container alignItems="center" spacing={2} className="padding-wrapper">
          <Grid
            item
            xs={12}
            sm={4}
            className={`centered-column-wrapper display-grid medium-padding ${classes.customHoverContainer}`}
          >
            <Typography
              className={classes.customTypoColor}
              fontWeight="bold"
              justifySelf="center"
            >
              Supprimer
            </Typography>
            <Typography className={classes.customTypoColor} justifySelf="center">
              {idsToDelete.length} joueurs
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            className={`centered-column-wrapper display-grid medium-padding ${classes.customHoverContainer}`}
          >
            <Typography
              className={classes.customTypoColor}
              fontWeight="bold"
              justifySelf="center"
            >
              Modifier la feuille de match de
            </Typography>
            <Typography className={classes.customTypoColor} justifySelf="center">
              {matchesUpdated} matchs
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            className={`centered-column-wrapper display-grid medium-padding ${classes.customHoverContainer}`}
          >
            <Typography
              className={classes.customTypoColor}
              fontWeight="bold"
              justifySelf="center"
            >
              Modifier les acteurs de
            </Typography>
            <Typography className={classes.customTypoColor} justifySelf="center">
              {gamefactsUpdated} faits de jeu
            </Typography>
          </Grid>
        </Grid>

        <Box className="medium-padding">
          {mustSelectCurrentClub && !selectedTeam && (
            <Typography
              className={classes.customTypoColor}
              justifySelf="center"
              fontSize="small"
              fontStyle="italic"
            >
              Veuillez sélectionner une équipe (club) actuelle pour poursuivre
            </Typography>
          )}

          <Box className="centered-wrapper small-padding">
            <DsasButton
              disabled={mustSelectCurrentClub && !selectedTeam}
              label={"Valider la fusion"}
              icon={faCheck}
              onClick={executeMerge}
              variant="alternative"
            />
          </Box>
        </Box>
      </Box>

      <Dialog open={openConfirmation} onClose={() => setOpenConfirmation(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous certain de valider la fusion ? Vous ne pourrez pas revenir en arrière.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmation(false)} color="secondary">
            Annuler
          </Button>
          <Button
            onClick={async () => {
              console.log(
                  teamsSeasonDraft.map((t) => ({
                    team: t.team?.name,
                    startISO: t.startDate?.toISOString?.(),
                    startFR: t.startDate?.toLocaleDateString?.("fr-FR"),
                    endISO: t.endDate?.toISOString?.(),
                    endFR: t.endDate?.toLocaleDateString?.("fr-FR"),
                    auto: t._autoEndDate,
                  }))
                );
              await mergePlayers(
                { ...mergedPlayer, teamsSeason: teamsSeasonDraft },
                idsToDelete,
                selectedTeam
              );
              setOpenConfirmation(false);
              navigate(returnTo);
            }}
            color="primary"
            variant="contained"
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MergePlayersPage;
